import { getItem, removeItem } from '../storage';

export const getFeatureFlagSync = (flagKey: string): boolean => {
  try {
    const userId = getUserId();
    if (!userId) { return false; }

    const storedData = getItem('featureFlags');
    
    if (storedData && storedData.flags && storedData.timestamp) {
      const age = Date.now() - storedData.timestamp;
      // too old, but still using the cached data and just logging a warning
      if (age > 5 * 60 * 1000) { // 5 minutes
        // tslint:disable-next-line:no-console
        console.log('Feature flag cache is expired. Should refresh but still using cached data');
      }

      return Boolean(storedData.flags[flagKey]);
    }
  } catch (error) {
    // tslint:disable-next-line:no-console
    console.error('Failed to get feature flag synchronously:', error);
  }
  return false;
};

/**
 * Initialize feature flags after successful login
 * Call this after rememberAuth() in your login flows
 */
export const initializeFeatureFlagsAfterLogin = async (): Promise<void> => {
  try {
    // tslint:disable-next-line:no-console
    console.debug('Initializing feature flags after login');
    
    // Get current hotel selection after login
    const brandHotelSelection = getItem('brandHotelSelection');
    const hotelId = brandHotelSelection ? brandHotelSelection.hotelId : undefined;
    
    // Fetch fresh flags for the logged-in user with hotel context
    await fetchFeatureFlags(hotelId, true); // Force refresh after login
    
    // tslint:disable-next-line:no-console
    console.debug('Feature flags initialized successfully after login');
  } catch (error) {
    // tslint:disable-next-line:no-console
    console.error('Failed to initialize feature flags after login:', error);
  }
};

/**
 * Clean up feature flags on logout
 * Call this before clearing user data in your logout flows
 */
export const cleanupFeatureFlagsOnLogout = (): void => {
  // tslint:disable-next-line:no-console
  console.debug('Cleaning up feature flags on logout');
  
  // Clear all feature flag cache from localStorage
  removeItem('featureFlags');
  
  // Dispatch event to notify components
  try {
    const event = new CustomEvent('featureFlagsUpdated', {
      detail: { flags: {} },
    });
    document.dispatchEvent(event);
    // tslint:disable-next-line:no-console
    console.debug('Feature flags cleanup event dispatched');
  } catch (error) {
    // tslint:disable-next-line:no-console
    console.error('Failed to dispatch feature flags cleanup event:', error);
  }
};

/**
 * Refresh feature flags when hotel changes
 * Call this when the user switches hotels
 */
export const refreshFeatureFlagsOnHotelChange = async (hotelId?: number): Promise<void> => {
  try {
    // tslint:disable-next-line:no-console
    console.debug('Refreshing feature flags on hotel change to:', hotelId);
    await fetchFeatureFlags(hotelId, true);
  } catch (error) {
    // tslint:disable-next-line:no-console
    console.error('Failed to refresh feature flags on hotel change:', error);
  }
};

/**
 * Refresh feature flags when brand changes
 * Call this when the user switches brands
 */
export const refreshFeatureFlagsOnBrandChange = async (): Promise<void> => {
  try {
    // tslint:disable-next-line:no-console
    console.debug('Refreshing feature flags on brand change');
    const brandHotelSelection = getItem('brandHotelSelection');
    const hotelId = brandHotelSelection ? brandHotelSelection.hotelId : undefined;
    await fetchFeatureFlags(hotelId, true);
  } catch (error) {
    // tslint:disable-next-line:no-console
    console.error('Failed to refresh feature flags on brand change:', error);
  }
};

/**
 * Fetch feature flags from the API
 */
const fetchFeatureFlags = async (hotelId?: number, forceRefresh = false): Promise<void> => {
  const userId = getUserId();
  if (!userId) {
    // tslint:disable-next-line:no-console
    console.warn('No user ID available for feature flags');
    return;
  }

  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cachedFlags = loadFromStorage(userId, hotelId);
    if (cachedFlags) {
      // tslint:disable-next-line:no-console
      console.debug('Using cached feature flags');
      dispatchFlagUpdateEvent(cachedFlags);
      return;
    }
  }

  try {
    const url = hotelId 
      ? Env.API_URL + '/features/check?hotelId=' + hotelId
      : Env.API_URL + '/features/check';

    // tslint:disable-next-line:no-console
    console.debug('Fetching feature flags from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + (getItem('user') && getItem('user').token ? getItem('user').token : ''),
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        // tslint:disable-next-line:no-console
        console.warn('Unauthorized request for feature flags');
        return;
      }
      throw new Error('HTTP error! status: ' + response.status);
    }

    const data = await response.json();
    // tslint:disable-next-line:no-console
    console.debug('Feature flags fetched successfully:', data.flags);
    
    // Save to localStorage
    saveToStorage(userId, data.flags, hotelId);
    
    // Dispatch event to notify components
    dispatchFlagUpdateEvent(data.flags);
    
  } catch (error) {
    // tslint:disable-next-line:no-console
    console.error('Failed to fetch feature flags:', error);
  }
};

export const getUserId = (): number | null => {
  try {
    const userData = getItem('user');
    return (userData && userData.user && userData.user.id) ? userData.user.id : null;
  } catch (error) {
    // tslint:disable-next-line:no-console
    console.error('Failed to get user ID:', error);
    return null;
  }
};

const loadFromStorage = (userId: number, hotelId?: number): any => {
  try {
    const storedData = getItem('featureFlags');
    
    if (storedData && storedData.flags && storedData.timestamp) {
      const age = Date.now() - storedData.timestamp;
      // still valid, so using the cached data
      if (age < 5 * 60 * 1000) { // 5 minutes
        return storedData.flags;
      }
    }
  } catch (error) {
    // tslint:disable-next-line:no-console
    console.error('Failed to load feature flags from storage:', error);
  }
  return null;
};

const saveToStorage = (userId: number, flags: any, hotelId?: number): void => {
  try {
    const cacheKey = 'featureFlags';
    const storageData = {
      flags,
      timestamp: Date.now(),
      userId,
      hotelId: hotelId || null,
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(storageData));
    // tslint:disable-next-line:no-console
    console.debug('Feature flags saved to localStorage with key:', cacheKey);
  } catch (error) {
    // tslint:disable-next-line:no-console
    console.error('Failed to save feature flags to storage:', error);
  }
};

const dispatchFlagUpdateEvent = (flags: any): void => {
  try {
    const event = new CustomEvent('featureFlagsUpdated', {
      detail: { flags },
    });
    document.dispatchEvent(event);
    // tslint:disable-next-line:no-console
    console.debug('Feature flags update event dispatched with flags:', flags);
  } catch (error) {
    // tslint:disable-next-line:no-console
    console.error('Failed to dispatch feature flags update event:', error);
  }
};
